import dayjs from "dayjs";
import { formatToLocalDate } from "utils/functions/formatToLocalDate";


export interface DrillSmartModalData {
  content?: any;
  drillSmartResult?: any;
  drillSmartLaunchUrl?: string | null;
  groupName?: string;
  nextDrill?: any;
  isLocked?: boolean;
}

export type DrillSmartRowIconType =
  | "lock"
  | "empty-circle"
  | "select"
  | "yellow-timer";

export interface DrillSmartRowDisplay {
  textIconLabel: string;
  iconType: DrillSmartRowIconType;
  middleLabel: string;
  badge: string;
  contentObjectType: string;
  status: string;
  drillSmartResult: any;
}

// Returns true if the work item is a DrillSmart grouping (not an individual content item)
export function isDrillSmartGrouping(work: any): boolean {
  return work?._isDrillSmartGrouping === true;
}


export function normalizeWorkToModalData(work: any): DrillSmartModalData {
  return {
    content: work?.Content ?? work?.content,
    drillSmartResult: work?.DrillSmartResult ?? work?.drillSmartResult,
    drillSmartLaunchUrl: work?.LaunchUrl ?? work?.launchUrl ?? null,
    groupName: work?.GroupName ?? work?.groupName ?? "",
    nextDrill: work?.NextDrill ?? work?.nextDrill,
    isLocked: work?.IsLocked === true,
  };
}

// Get DrillSmartResult status from a work item
function getDrillSmartStatus(work: any): string {
  return (
    work?.DrillSmartResult?.Status ??
    work?.drillSmartResult?.Status ??
    "NotStarted"
  );
}

// Map DrillSmart status to row icon type
function drillSmartStatusToIconType(status: string): DrillSmartRowIconType {
  if (status === "Completed" || status === "Complete") return "select";
  if (status === "InProgress" || status === "In Progress") return "yellow-timer";
  return "empty-circle";
}

// Map DrillSmart status to middle label text
function drillSmartStatusToMiddleLabel(status: string): string {
  if (status === "Completed" || status === "Complete") return "Completed";
  if (status === "InProgress" || status === "In Progress") return "In Progress";
  return "Not Started";
}

// Returns display props for a row when the item is a DrillSmart grouping
export function getDrillSmartRowDisplay(
  work: any,
  options?: {
    formatDateCompleted?: (date: any) => string;
  }
): DrillSmartRowDisplay {
  // Get the actual status from DrillSmartResult
  const status = getDrillSmartStatus(work);
  const actualStatusLabel = drillSmartStatusToMiddleLabel(status);
  
  // Check if DrillSmartGrouping is locked - use lock icon but preserve actual status label
  if (work?.IsLocked === true) {
    return {
      textIconLabel: work?.GroupName ?? work?.groupName ?? "",
      iconType: "lock",
      middleLabel: actualStatusLabel,
      badge: "DrillSmart",
      contentObjectType: "DrillSmart",
      status: status,
      drillSmartResult: work?.DrillSmartResult ?? work?.drillSmartResult,
    };
  }
  
  return {
    textIconLabel: work?.GroupName ?? work?.groupName ?? "",
    iconType: drillSmartStatusToIconType(status),
    middleLabel: actualStatusLabel,
    badge: "DrillSmart",
    contentObjectType: "DrillSmart",
    status: "InProgress",
    drillSmartResult: work?.DrillSmartResult ?? work?.drillSmartResult,
  };
}

export function getRowDisplayForWork(
  work: any,
  options?: {
    formatDateCompleted?: (date: any) => string;
    contentObjectTypeResolver?: (work: any) => string;
    treatAsDrillSmart?: boolean;
  }
): DrillSmartRowDisplay & { isDrillSmart: boolean } {
  if (options?.treatAsDrillSmart || isDrillSmartGrouping(work)) {
    const drill = getDrillSmartRowDisplay(work, options);
    return { ...drill, isDrillSmart: true };
  }
  const formatDate = options?.formatDateCompleted ?? ((d: any) => (d ? formatToLocalDate(d, "MM/DD/YYYY") : ""));
  const status = work?.Status ?? "Not Started";
  const iconType: DrillSmartRowIconType =
    work?.Status === "Lock" || work?.IsLocked === true
      ? "lock"
      : status === "Completed" || status === "Complete"
        ? "select"
        : status === "Not Started"
          ? "empty-circle"
          : "yellow-timer";
  const middleLabel =
    status === "Completed" || status === "Complete"
      ? `Completed on ${formatDate(work?.DateCompleted)}`
      : status === "Not Started"
        ? "Not Started"
        : status === "Lock"
          ? "Not Started"
          : "In Progress";
  const contentObjectType = options?.contentObjectTypeResolver
    ? options.contentObjectTypeResolver(work)
    : work?.ContentObjectType ?? "";
  return {
    textIconLabel: work?.Name ?? "",
    iconType,
    middleLabel,
    badge: "",
    contentObjectType,
    status,
    drillSmartResult: undefined,
    isDrillSmart: false,
  };
}
